/* eslint-disable max-lines */
import {
    Alert,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material';
import React, { ChangeEvent } from 'react';

import { MccMatchType } from 'app/components/form-controls/mcc-match-type';
import { LocationMatchTypeComp } from 'app/components/location-match-type-comp';
import { MatchTypeComp } from 'app/components/match-type-comp';
import { Translate } from 'app/components/translate';
import { Channel } from 'app/constants/channel';
import { CrisRequestType } from 'app/constants/cris-request-type';
import { AppContext } from 'app/context/app-context';
import { AppContextProps } from 'app/context/app-context-props';
import { AppConfigName } from 'app/enum/app-config-name';
import { AppConfig } from 'app/model/app-config';
import { PatternData } from 'app/model/pattern-data';
import { Strings } from 'app/utils/strings';

type UnifiedEventType =
    | ChangeEvent<{ name?: string; value: unknown }>
    | (Event & { target: { value: unknown; name: string } });

interface MatchTableProps {
    type: CrisRequestType;
    data: PatternData;
    style?: React.CSSProperties;
    onChange: (evt: UnifiedEventType) => void;
}

interface MatchTableState {
    formVisible: boolean;
}

export class MatchTable extends React.Component<MatchTableProps, MatchTableState> {
    public static contextType = AppContext;

    private static parensMatch = new RegExp('\\([^)(]+\\)', 'g');

    private selectionCache: Array<{
        target: HTMLInputElement | HTMLTextAreaElement;
        start: number | null;
        end: number | null;
    }> = [];

    public constructor(props: MatchTableProps | Readonly<MatchTableProps>) {
        super(props);

        this.state = {
            formVisible: true,
        };
    }

    public componentDidUpdate(): void {
        this.selectionCache.forEach((selectionData) => {
            // since parent component normalizes input,
            // we need to restore caret position, otherwise it always jumps to the end
            selectionData.target.setSelectionRange(selectionData.start, selectionData.end);
        });

        this.selectionCache = [];
    }

    public render(): React.ReactNode {
        const data = this.props.data;

        const illegalChars = (this.context as AppContextProps).appConfigs[AppConfigName.ILLEGAL_CHARS];

        const decoratedOnUpdate = (evt: UnifiedEventType) => {
            const target = evt.target as HTMLInputElement | HTMLTextAreaElement;
            if (target && target.selectionStart) {
                this.selectionCache.push({ target, start: target.selectionStart, end: target.selectionEnd });
            }
            this.props.onChange(evt);
        };

        return (
            <TableContainer component={Paper} style={this.props.style}>
                <Table
                    size='small'
                    className='no-bottom-lines'
                    sx={[
                        {
                            '& td:nth-of-type(2n+3), th:nth-of-type(2n+3)': {
                                borderLeft: '1px solid text.disabled',
                            },
                        },
                    ]}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>Match Value</TableCell>
                            <TableCell>Match Type</TableCell>
                            <TableCell>Match Value</TableCell>
                            <TableCell>Match Type</TableCell>
                            <TableCell>Match Value</TableCell>
                            <TableCell>Match Type</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    name='midPattern'
                                    type='text'
                                    label={Strings.patternDataNames('midPattern')}
                                    variant='outlined'
                                    size='small'
                                    value={data.midPattern || ''}
                                    onChange={decoratedOnUpdate}
                                    {...this.checkIllegalChars(data.midPattern, illegalChars)}
                                />
                            </TableCell>
                            <TableCell>
                                <MatchTypeComp
                                    name='matchOperator'
                                    value={data.matchOperator}
                                    includeNot={this.props.type === CrisRequestType.STORE}
                                    variant='outlined'
                                    size='small'
                                    onChange={decoratedOnUpdate}
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    name='cityPattern'
                                    type='text'
                                    label={Strings.patternDataNames('cityPattern')}
                                    variant='outlined'
                                    size='small'
                                    value={data.cityPattern || ''}
                                    onChange={decoratedOnUpdate}
                                    {...this.checkIllegalChars(data.cityPattern, illegalChars)}
                                />
                            </TableCell>
                            <TableCell>{this.getMatchTypeForGeo(decoratedOnUpdate)}</TableCell>

                            <TableCell>
                                <TextField
                                    fullWidth
                                    name='statePattern'
                                    type='text'
                                    label={Strings.patternDataNames('statePattern')}
                                    variant='outlined'
                                    size='small'
                                    value={data.statePattern || ''}
                                    onChange={decoratedOnUpdate}
                                    {...this.checkIllegalChars(data.statePattern, illegalChars)}
                                />
                            </TableCell>
                            <TableCell>{this.getMatchTypeForGeo(decoratedOnUpdate)}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    name='descPattern'
                                    type='text'
                                    label={Strings.patternDataNames('descPattern')}
                                    variant='outlined'
                                    size='small'
                                    value={data.descPattern || ''}
                                    onChange={decoratedOnUpdate}
                                    {...this.checkIllegalChars(data.descPattern, illegalChars)}
                                />
                            </TableCell>
                            <TableCell>
                                <MatchTypeComp
                                    name='matchOperator'
                                    value={data.matchOperator}
                                    includeNot={this.props.type === CrisRequestType.STORE}
                                    variant='outlined'
                                    size='small'
                                    onChange={decoratedOnUpdate}
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    name='zipPattern'
                                    type='text'
                                    label={Strings.patternDataNames('zipPattern')}
                                    variant='outlined'
                                    size='small'
                                    value={data.zipPattern || ''}
                                    onChange={decoratedOnUpdate}
                                    {...this.checkIllegalChars(data.zipPattern, illegalChars)}
                                />
                            </TableCell>
                            <TableCell>{this.getMatchTypeForGeo(decoratedOnUpdate)}</TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    name='countryPattern'
                                    type='text'
                                    disabled={this.props.type !== CrisRequestType.STORE}
                                    label={Strings.patternDataNames('countryPattern')}
                                    variant='outlined'
                                    size='small'
                                    value={data.countryPattern || ''}
                                    onChange={decoratedOnUpdate}
                                    {...this.checkIllegalChars(data.countryPattern, illegalChars)}
                                />
                            </TableCell>
                            <TableCell>{this.getMatchTypeForGeo(decoratedOnUpdate)}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    name='mccPattern'
                                    type='text'
                                    label={Strings.patternDataNames('mccPattern')}
                                    variant='outlined'
                                    size='small'
                                    value={data.mccPattern.join(',')}
                                    onChange={decoratedOnUpdate}
                                    placeholder='comma delimited list'
                                    {...this.checkIllegalChars(data.mccPattern.join(','), illegalChars)}
                                />
                            </TableCell>
                            <TableCell>
                                <MccMatchType
                                    name='mccMatchOperator'
                                    value={data.mccMatchOperator}
                                    variant='outlined'
                                    size='small'
                                    onChange={decoratedOnUpdate}
                                    // disabled={this.props.type !== CrisRequestType.STORE}
                                    // TODO temporary disable until seam backend is ready for this
                                    disabled
                                />
                            </TableCell>
                            {this.getExtraFields(illegalChars)}
                        </TableRow>
                        <TableRow>
                            <TableCell colSpan={9}>{this.props.children}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    private getMatchTypeForGeo(onUpdate: (evt: UnifiedEventType) => void): React.ReactNode {
        if (this.props.type === CrisRequestType.STORE) {
            return (
                <LocationMatchTypeComp
                    name='locationOperator'
                    value={this.props.data.locationOperator}
                    variant='outlined'
                    size='small'
                    onChange={onUpdate}
                />
            );
        }

        return this.props.data.matchOperator;
    }

    private getExtraFields(illegalChars: AppConfig) {
        const data = this.props.data;

        switch (this.props.type) {
            case CrisRequestType.CHANNEL:
                return (
                    <React.Fragment>
                        <TableCell>
                            <FormControl variant='outlined' size='small' fullWidth>
                                <InputLabel>{Strings.patternDataNames('subChannel')}</InputLabel>
                                <Select
                                    name='subChannel'
                                    value={data.subChannel || ''}
                                    onChange={this.props.onChange}
                                    label={Strings.patternDataNames('subChannel')}
                                >
                                    <MenuItem value={Channel.ONLINE}>Online</MenuItem>
                                    <MenuItem value={Channel.BM}>B&M</MenuItem>
                                </Select>
                            </FormControl>
                        </TableCell>
                        <TableCell>Set To</TableCell>
                        <TableCell />
                        <TableCell />
                    </React.Fragment>
                );
            case CrisRequestType.LOCATION:
                return (
                    <React.Fragment>
                        <TableCell>
                            <FormControl fullWidth variant='outlined' size='small'>
                                <InputLabel id='extract-field-label'>
                                    {Strings.patternDataNames('extractField')}
                                </InputLabel>
                                <Select
                                    name='extractField'
                                    labelId='extract-field-label'
                                    value={data.extractField || ''}
                                    onChange={this.props.onChange}
                                    label={Strings.patternDataNames('extractField')}
                                >
                                    <MenuItem value='merchdesc'>merchdesc</MenuItem>
                                    <MenuItem value='merchcity'>merchcity</MenuItem>
                                    <MenuItem value='mid'>mid</MenuItem>
                                    <MenuItem value='merchstate'>merchstate</MenuItem>
                                    <MenuItem value='merchzip'>merchzip</MenuItem>
                                    <MenuItem value='mcc'>mcc</MenuItem>
                                </Select>
                            </FormControl>
                        </TableCell>
                        <TableCell>Equal</TableCell>
                        <TableCell>
                            <TextField
                                fullWidth
                                name='extractPattern'
                                type='text'
                                label={Strings.patternDataNames('extractPattern')}
                                variant='outlined'
                                size='small'
                                value={data.extractPattern || ''}
                                onChange={this.props.onChange}
                                {...this.checkIllegalChars(data.extractPattern, illegalChars)}
                            />
                        </TableCell>
                        <TableCell>RegEx</TableCell>
                    </React.Fragment>
                );
            default:
                return (
                    <React.Fragment>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell />
                    </React.Fragment>
                );
        }
    }

    private checkBracketBalance(pattern: string): number {
        let balance = 0;
        let ignoreNextChar = false;
        for (const letter of pattern) {
            if (ignoreNextChar) {
                ignoreNextChar = false;
                continue;
            } else {
                switch (letter) {
                    case '\\': {
                        ignoreNextChar = true;
                        break;
                    }
                    case '[': {
                        balance++;
                        break;
                    }
                    case ']': {
                        balance--;
                        break;
                    }
                    // No default
                }
            }
        }

        return balance;
    }
    private removeAllCharsBetweenBrackets(pattern: string): string {
        let pipeCheckWithoutBrackets = '';
        let shouldRemove = false;
        let ignoreNextChar = false;
        for (const letter of pattern) {
            if (ignoreNextChar) {
                ignoreNextChar = false;
            } else {
                switch (letter) {
                    case '\\': {
                        ignoreNextChar = true;
                        break;
                    }
                    case '[': {
                        shouldRemove = true;
                        continue;
                    }
                    case ']': {
                        shouldRemove = false;
                        continue;
                    }
                    // No default
                }
            }
            if (!shouldRemove) {
                pipeCheckWithoutBrackets += letter;
            }
        }

        return pipeCheckWithoutBrackets;
    }
    private checkIllegalChars(midPattern: string | undefined, appConfig: AppConfig) {
        if (!midPattern) {
            return {};
        }

        let pipeCheck = midPattern;
        const balance = this.checkBracketBalance(pipeCheck);

        if (0 === balance) {
            pipeCheck = this.removeAllCharsBetweenBrackets(pipeCheck);
        }

        while (MatchTable.parensMatch.exec(pipeCheck)) {
            pipeCheck = pipeCheck.replaceAll(MatchTable.parensMatch, '');
        }
        // Remove all matching [.*] pairs with nesting
        const bracketMatchPat = new RegExp('\\[[^\\]\\[]+\\]', 'g');
        while (pipeCheck.match(bracketMatchPat)) {
            pipeCheck = pipeCheck.replaceAll(bracketMatchPat, '');
        }

        if (/\|/.exec(pipeCheck)) {
            return {
                error: true,
                helperText: <Translate tkey='illegalChar.onePipe' />,
            };
        }

        // Warn on any unmatched bracket. => Warn of any unescaped unmatched bracket
        // if (/\[/.exec(pipeCheck) || /\]/.exec(pipeCheck)) {
        if (0 !== balance) {
            return {
                error: true,
                helperText: (
                    <Alert variant='outlined' severity='warning'>
                        <Translate tkey='illegalChar.mismatchedBracket' />
                    </Alert>
                ),
            };
        }
        // location and channel module knows how to special characters
        if (CrisRequestType.STORE !== this.props.type) {
            return {};
        }

        for (const char of appConfig.value as string[]) {
            if (midPattern.includes(char)) {
                return {
                    error: true,
                    helperText: `Character ${char} is not allowed.`,
                };
            }
        }

        return {};
    }
}
